import {AttrsSchema, Component, ConfigOptions} from 'panel';
import type {Moment} from 'moment';
import moment from 'moment';
import takeRight from 'lodash/takeRight';
import range from 'lodash/range';
import omit from 'lodash/omit';

import {CollapsibleTheme} from 'common/components/section';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {eventPropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {currentProjectStore} from 'common/report/stores/project-details';
import {TrackingUsageContext} from 'common/report/tracking-setup';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {mapToSerializableObj} from 'common/util';
import {AsyncCountingSemaphore} from 'common/util/async-counting-semaphore';
import {localizedMomentToProjectMoment} from 'common/util/date';
import {emitTrackEvent} from 'common/util/dom';
import {MPProperties} from 'common/constants';
import {
  epochTimestampToProjectTimeZone,
  FormattedProperty,
  formatUserProperties,
  getRawPropertyName,
  getRawPropertyValue,
} from 'common/util/format-property';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {extractHashParams, getInsightsLinkUrl, groupNeighboringEvents} from 'common/report/util/profile/utils';
import {
  ActivityStreamQueryResult,
  ProfileQueryEndpoint,
  ProfileQueryParameters,
  REPORT_NAME,
  UnformattedUserEvent,
} from 'common/report/util/profile/types';
import {getActivityStreamFieldsToUpdate} from 'common/widgets/profile-activity/util/activity-streams';
import {profileClient} from 'common/report/queries/query-api/profile-client';
import {TrackedEvents, TrackedProperties} from 'common/report/util/profile/tracking';
import {ProjectFeatureFlag, PROPERTIES, SHOW_LAST_X_DAYS_FEATURE_FLAGS} from 'common/report/constants';
import {parseJsUrl, serializeJsUrl} from 'common/report/util/url';
import {
  ActivityDayGroup,
  ActivityGroup,
  DATE_FORMAT_STRING,
  DEFAULT_NUMBER_OF_EVENTS_TO_DISPLAY,
  DISPLAY_DATE_FORMAT_STRING,
  EVENT_DISPLAY_SIZE_INCREMENT,
  FormattedUserEvent,
  INITIAL_NUM_EVENTS_LIMIT,
  NUM_PREVIEW_PROPERTIES,
  ProfileActivityAttrs,
  ProfileActivityState,
  ProfileActivityURLState,
  SINCE_DATE_FORMAT_STRING,
} from 'common/widgets/profile-activity/types';
import {
  PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS,
  PresetDateRange,
  SHOW_LAST_X_DAYS_PRESET_DATE_RANGE,
} from 'common/widgets/date-range-picker/models/presets';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';

// Import child components to register them
import {template} from './template';
import './index.styl';

const getDateRange = () => {
  const showLastXDaysFlag = SHOW_LAST_X_DAYS_FEATURE_FLAGS.filter((flag: ProjectFeatureFlag) =>
    currentProjectStore.value?.hasFeatureFlag(flag),
  );

  return showLastXDaysFlag
    ? SHOW_LAST_X_DAYS_PRESET_DATE_RANGE[showLastXDaysFlag[0]] ?? PresetDateRange.Last30Days
    : PresetDateRange.Last30Days;
};

const getDefaultProfileActivityState = () => ({
  dateRange: new DateRange(PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[getDateRange()]),
  earliestTime: null,
  enableShowMoreButton: true,
  error: false,
  renderedGroupings: new Map(),
  includedEvents: [],
  excludedEvents: [],
  numberOfEventsDisplayed: DEFAULT_NUMBER_OF_EVENTS_TO_DISPLAY,
  isAtHistoryLimit: false,
  loading: {
    initial: true,
    eventDefinitions: true,
    moreEvents: false,
    propertyDefinitions: true,
  },
  numEventsToRequest: INITIAL_NUM_EVENTS_LIMIT,
  rawEvents: [],
  latestDateToFetch: null,
});

export interface ProfileActivityJsxProps {
  attrs?: ProfileActivityAttrs;
}

export class ProfileActivity extends Component<ProfileActivityState, ProfileActivityAttrs> {
  private queryApiPool: AsyncCountingSemaphore;
  private updateCallback = () => this.update();

  static get attrsSchema(): AttrsSchema<ProfileActivityAttrs> {
    return {
      ...super.attrsSchema,
      'data-group-id': `string`,
      'distinct-id': `string`,
      'history-limit-days': `number`,
      'sidebar-mode': `boolean`,
      'utc-offset': `number`,
      timezone: `string`,
      'profile-email': `string`,
      'from-date': {
        type: `string`,
        default: ``,
      },
      'to-date': {
        type: `string`,
        default: ``,
      },
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
    };
  }

  get config(): ConfigOptions<ProfileActivityState> {
    return {
      defaultState: getDefaultProfileActivityState(),
      template,
      helpers: {
        range,
        CollapsibleTheme,
        REPORT_NAME,
        NUM_PREVIEW_PROPERTIES,
        MPProperties,
        getRawPropertyValue,
        getRawPropertyName,
        mapToSerializableObj,
        getProjectId,
        getWorkspaceId,
        getDisplayTime: (secondsFromEpoch): string => {
          return epochTimestampToProjectTimeZone(secondsFromEpoch * 1000, this.attr(`timezone`)).format(`h:mm:ss A`);
        },
        getLastVisibleFromDate: (): string => {
          const visibleFilteredEvents = this.getFilteredVisibleEvents();

          const lastVisibleTime = visibleFilteredEvents[0].properties[`time`];

          return epochTimestampToProjectTimeZone(lastVisibleTime * 1000, this.attr(`timezone`)).format(
            SINCE_DATE_FORMAT_STRING,
          );
        },
        handleDateRangeSelected: ({detail}) => {
          const dateRange = new DateRange(detail);

          this.update({
            dateRange,
            rawEvents: [],
            loading: {
              ...this.state.loading,
              initial: true,
            },
            latestDateToFetch: dateRange.getCalendarDatesAsOf(this.attr(`project-as-of-date`)).to,
          });
          this.updateJSURL(this.getJSURLFilters());
          this.loadUserActivity();
        },
        getSelectedHiddenEvents: () => {
          const {includedEvents, excludedEvents} = this.state;

          return includedEvents.length ? includedEvents : excludedEvents;
        },
        updateEventFilter: (ev): void => {
          const {action, selected, unselected} = ev.detail;
          const {
            loading: {eventDefinitions, propertyDefinitions},
            dateRange,
          } = this.state;

          let excludedEvents = [];
          let includedEvents = [];

          if (action === `show`) {
            includedEvents = unselected;
          } else if (action === `hide`) {
            excludedEvents = selected;
          }

          // Reset local state minus the date and re-fetch
          const defaultProfileActivityState = getDefaultProfileActivityState();
          this.update({
            ...defaultProfileActivityState,
            dateRange,
            loading: {
              ...defaultProfileActivityState.loading,
              eventDefinitions,
              propertyDefinitions,
            },
            includedEvents,
            excludedEvents,
            latestDateToFetch: dateRange.getCalendarDatesAsOf(this.attr(`project-as-of-date`)).to,
          });

          this.updateJSURL(this.getJSURLFilters());

          this.loadUserActivity();
        },
        getUserActivityDisplay: (): ActivityDayGroup[] => {
          return this.groupActivityByDayAndEvent(this.renameAndFormatEvents(this.getFilteredVisibleEvents()));
        },
        handleShowMore: async (): Promise<void> => {
          this.trackEvent(TrackedEvents.ShowMoreEvents, this.activityStreamTrackingProperties());

          const {numberOfEventsDisplayed, rawEvents} = this.state;

          if (numberOfEventsDisplayed + EVENT_DISPLAY_SIZE_INCREMENT <= rawEvents.length) {
            /* TODO(sam) fix performance for this report
             * As is, it currently loads very slow when we hit `Show More`. This is due to the fact that
             * `mp-section` takes in a slot, so the slotted component is rendered incl lifecycle, but not displayed,
             * even if it is hidden. This means that every single new event we show by increasing the display window size
             * is rendered, and all of their properties are rendered too, which can be relatively slow (1s browser scripting
             * time for ~100 new events)
             * To patch this, we send a separate update to show the spinner so that there is _some_ feedback
             * to the user that we are loading more events.
             * When performance is fixed, this can be deleted (remove requestAnimationFrame, only update window size
             * as the load should be instant (well, the browser frame won't be blocked by rendering a billion panel components).
             *
             * A pass at this resulted in reducing the property formatting to a jsx mixin, which helped alleviate
             * performance costs from rendering a full panel component, but future improvement is likely needed.
             *
             * A future improvement could be having mp-section take in a `slotRenderer` that is called when it is opened
             */

            this.update({
              loading: {
                ...this.state.loading,
                moreEvents: true,
              },
            });

            requestAnimationFrame(() => {
              this.update({
                numberOfEventsDisplayed: numberOfEventsDisplayed + EVENT_DISPLAY_SIZE_INCREMENT,
                loading: {
                  ...this.state.loading,
                  moreEvents: false,
                },
              });
            });
          } else if (this.state.numEventsToRequest > this.state.rawEvents.length) {
            this.update({
              numberOfEventsDisplayed: numberOfEventsDisplayed + EVENT_DISPLAY_SIZE_INCREMENT,
              enableShowMoreButton: false,
            });
          } else {
            this.update({
              loading: {
                ...this.state.loading,
                moreEvents: true,
              },
              numberOfEventsDisplayed: numberOfEventsDisplayed + EVENT_DISPLAY_SIZE_INCREMENT,
            });
            this.prepareStateForExtraEventLoad();

            await this.loadUserActivity();
            this.update({
              loading: {
                ...this.state.loading,
                moreEvents: false,
              },
            });
          }
        },
        viewInInsights: () => {
          this.trackEvent(TrackedEvents.SeeUserInInsights, {
            ...this.activityStreamTrackingProperties(),
            'Profile Email': this.attr(`profile-email`),
          });

          window.open(
            getInsightsLinkUrl({
              projectId: getProjectId(),
              workspaceId: getWorkspaceId(),
              distinctId: this.attr(`distinct-id`),
              dateRange: this.state.dateRange,
              includedEvents: this.state.includedEvents,
              excludedEvents: this.state.excludedEvents,
              dataGroupId: String(this.attr(`data-group-id`)),
            }),
            `_blank`,
          );
        },
        handleExpandGrouping: (ev: Event, activity: ActivityGroup): void => {
          const {renderedGroupings} = this.state;

          const {eventName, eventGroup} = activity;

          const {insertId, time} = eventGroup[0];

          renderedGroupings.set(insertId, true);

          this.update({
            renderedGroupings,
          });

          this.helpers.trackCollapsibleChange(ev, eventName, time, eventGroup.length > 1);
        },
        handleQueryBuilderBlockTrack: (ev: CustomEvent) => {
          const {props, eventName} = ev.detail;
          ev.stopPropagation();

          this.trackEvent(`${eventName}`, {
            ...props,
            [`usage context`]: TrackingUsageContext.Report,
          });
        },
        trackCollapsibleChange: (ev, eventName: string, time: string, isGroupedEvent: boolean): void => {
          this.trackEvent(TrackedEvents.UserEventExpanded, {
            ...this.activityStreamTrackingProperties(),
            Datetime: time,
            'Event Name': eventName,
            Grouped: isGroupedEvent,
            'Toggle type': ev.detail.state === `open` ? `Show` : `Hide`,
          });
        },
      },
      routes: {
        ':jsurl': (_, params) => {
          // ignore distinct_id and data_group_id prefix to parse out activity stream url filters
          const {jsUrl} = extractHashParams(params);
          // Only update state if we have a jsurl to prevent redirecting to an empty route
          if (jsUrl) {
            return this.handleJSURLRoute(jsUrl);
          }
          return null;
        },
        '': () => ({}), // don't update state on anything but a jsurl
      },
    };
  }

  prefixJSURL(jsUrlStr): string {
    const distinctId = this.attr(`distinct-id`);
    const dataGroupIdParam = this.attr(`data-group-id`) ? `data_group_id=${this.attr(`data-group-id`)}&` : ``;
    return `#distinct_id=${distinctId}&${dataGroupIdParam}${jsUrlStr}`;
  }

  updateJSURL(urlData) {
    history.replaceState(null, ``, this.prefixJSURL(serializeJsUrl(urlData)));
  }

  // TODO are we just gonna copy-paste this crap everywhere the mixin is used???
  handleJSURLRoute(rawJsURL) {
    let stateUpdate = {};

    const jsUrlInfo = parseJsUrl(rawJsURL);
    if (jsUrlInfo.valid) {
      if (jsUrlInfo.correctedUrl !== rawJsURL) {
        history.replaceState(null, ``, this.prefixJSURL(jsUrlInfo.correctedUrl));
        stateUpdate = {...stateUpdate, $fragment: jsUrlInfo.correctedUrl};
      }

      return {...stateUpdate, ...this.handleURLParamsUpdate(jsUrlInfo.data)};
    } else {
      this.navigate(``, {...stateUpdate, ...this.handleInvalidURLParams()});
      return stateUpdate;
    }
  }

  handleURLParamsUpdate(urlData: ProfileActivityURLState): ProfileActivityURLState {
    const {includedEvents, excludedEvents, dateRange} = urlData;

    return {
      ...(includedEvents ? {includedEvents} : {}),
      ...(dateRange ? {dateRange: new DateRange(dateRange)} : {}),
      ...(excludedEvents ? {excludedEvents} : {}),
    };
  }

  // Default state is fine if url filters are invalid
  handleInvalidURLParams(): ProfileActivityURLState {
    return {};
  }

  getJSURLFilters(): ProfileActivityURLState {
    const {dateRange, includedEvents, excludedEvents} = this.state;

    // Only include includedEvents or excluded events, or neither
    const eventFilters = {};
    if (includedEvents.length > 0) {
      eventFilters[`includedEvents`] = includedEvents;
    } else if (excludedEvents.length > 0) {
      eventFilters[`excludedEvents`] = excludedEvents;
    }

    return {
      ...eventFilters,
      dateRange,
    };
  }

  public connectedCallback(): void {
    this.queryApiPool = new AsyncCountingSemaphore(1);
    if (this.attr(`sidebar-mode`)) {
      this.setConfig(`routes`, {});
    }
    super.connectedCallback();

    currentProjectStore.subscribe(this.updateCallback);

    this.eventDefinitionsStoreCallback = this.eventDefinitionsStoreCallback.bind(this);
    eventDefinitionsStore.subscribe(this.eventDefinitionsStoreCallback).fetch({
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
    });

    this.eventPropertyDefinitionsStoreCallback = this.eventPropertyDefinitionsStoreCallback.bind(this);
    eventPropertyDefinitionsStore.subscribe(this.eventPropertyDefinitionsStoreCallback).fetch({
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
    });

    // Prevents a double fetch
    if (this.attr(`distinct-id`) && !this.queryApiPool.getCount()) {
      this.loadUserActivity();
    }
  }

  public disconnectedCallback(): void {
    eventDefinitionsStore.unsubscribe(this.eventDefinitionsStoreCallback);
    eventPropertyDefinitionsStore.unsubscribe(this.eventPropertyDefinitionsStoreCallback);
    currentProjectStore.unsubscribe(this.updateCallback);

    super.disconnectedCallback();
  }

  public attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    super.attributeChangedCallback(name, oldValue, newValue);

    // Prevent us from changing state/calling fetches during component mount
    if (!this.initialized || oldValue === newValue) {
      return;
    }

    switch (name) {
      case `data-group-id`: // fallthrough
      case `distinct-id`:
        this.update({
          ...omit(getDefaultProfileActivityState(), `dateRange`),
          loading: {
            ...this.state.loading,
            initial: true,
          },
        });
        // Since this is relatively rare, but could happen if we resolve to a different alias
        // ensure we show the loading state before triggering a fetch
        requestAnimationFrame(() => {
          this.loadUserActivity();
        });
        break;
      case `history-limit-days`:
        this.update({
          ...omit(getDefaultProfileActivityState(), `dateRange`),
          isAtHistoryLimit: false,
          loading: {
            ...this.state.loading,
            initial: true,
          },
        });
        this.loadUserActivity();
        break;
      case `project-as-of-date`:
        this.loadUserActivity();
        break;
    }
  }

  eventPropertyDefinitionsStoreCallback(): void {
    this.update({
      loading: {
        ...this.state.loading,
        propertyDefinitions: false,
      },
    });
  }

  eventDefinitionsStoreCallback(): void {
    this.update({
      loading: {
        ...this.state.loading,
        eventDefinitions: false,
      },
    });
  }

  private getFilteredVisibleEvents(): UnformattedUserEvent[] {
    const rawEvents: UnformattedUserEvent[] = this.state.rawEvents;

    return takeRight(rawEvents, this.state.numberOfEventsDisplayed);
  }

  private renameAndFormatEvents(events: UnformattedUserEvent[]): FormattedUserEvent[] {
    const timezone = this.attr(`timezone`);
    const dataGroupId = String(this.attr(`data-group-id`));
    const eventDefinitions = eventDefinitionsStore.value;
    const propertyDefinitions = eventPropertyDefinitionsStore.value;

    // Use any lexicon defined names/renames, specific mixpanel properties renames, or fallback to the property name
    const getDisplayName = (eventName: string): string =>
      eventDefinitions[eventName]?.displayName || eventDefinitions[eventName]?.name || eventName;

    const formatUserEvent = (userEvent: UnformattedUserEvent): FormattedUserEvent => {
      const eventName = getDisplayName(userEvent.event);
      const properties = formatUserProperties(userEvent.properties, {timezone, dataGroupId});

      const time = getRawPropertyValue(properties.get(`time`)) as number;
      const insertId = getRawPropertyValue(properties.get(MPProperties.InsertId)) as string;
      const userDefinedProperties: FormattedProperty[] = [];
      const mixpanelProperties: FormattedProperty[] = [];

      for (const [propertyName, property] of properties.entries()) {
        if (
          propertyDefinitions[propertyName]?.isMixpanelDefinition ||
          Object.prototype.hasOwnProperty.call(PROPERTIES, propertyName)
        ) {
          mixpanelProperties.push(property);
        } else {
          userDefinedProperties.push(property);
        }
      }

      return {
        eventDisplayName: eventName,
        insertId,
        mixpanelProperties,
        userDefinedEventProperties: userDefinedProperties,
        time,
      };
    };

    return events.map(formatUserEvent);
  }

  private prepareStateForExtraEventLoad(): void {
    const {earliestTime} = this.state;
    const earliestDay = moment(earliestTime * 1000).startOf(`day`);

    this.update({
      latestDateToFetch: earliestDay.format(DATE_FORMAT_STRING),
    });
  }

  private getHistoryLimit(): Moment {
    return moment().subtract(this.attr(`history-limit-days`), `days`);
  }

  private async loadUserActivity(): Promise<void> {
    let {
      dateRange,
      excludedEvents,
      includedEvents,
      numEventsToRequest,
      numberOfEventsDisplayed,
      latestDateToFetch,
    } = this.state;
    // This can happen in some weird cases during component initialization, skip the query until we have a valid limit
    if (!this.attr(`history-limit-days`) || !this.queryApiPool) {
      return;
    }

    if (!latestDateToFetch) {
      latestDateToFetch = dateRange.getCalendarDatesAsOf(this.attr(`project-as-of-date`)).to;
    }
    let toDate = moment(latestDateToFetch);
    let fromDate = moment(dateRange.getCalendarDatesAsOf(this.attr(`project-as-of-date`)).from);

    const historyLimit = this.getHistoryLimit();

    const utcOffset = this.attr(`utc-offset`);

    // Modify the dates query time to be local time to ensure we have a chance of rolling over to prev/next day
    toDate.set(`hour`, new Date().getHours());
    toDate.set(`minute`, new Date().getMinutes());
    fromDate.set(`hour`, new Date().getHours());
    fromDate.set(`minute`, new Date().getMinutes());

    // Modify moments to account for timezone differences
    localizedMomentToProjectMoment(toDate, utcOffset);
    localizedMomentToProjectMoment(fromDate, utcOffset);

    // don't query data past history limit
    if (!dateRange.isWithinMaxHistoryLimit(this.attr(`history-limit-days`))) {
      fromDate = historyLimit.clone();
    }

    if (toDate.isSameOrBefore(historyLimit, `day`)) {
      this.update({
        enableShowMoreButton: false,
        isAtHistoryLimit: true,
      });
      this.queryApiPool.release();
      return; // skip query
    }

    if (this.attr(`to-date`) && this.attr(`from-date`)) {
      toDate = moment(this.attr(`to-date`));
      fromDate = moment(this.attr(`from-date`));
    }

    if (await this.queryApiPool.acquire()) {
      const {error, events: newEvents} = await this.fetchUserActivity({
        fromDate: fromDate.format(DATE_FORMAT_STRING),
        toDate: toDate.format(DATE_FORMAT_STRING),
        projectId: getProjectId(),
        workspaceId: getWorkspaceId(),
        distinctIDs: [this.attr(`distinct-id`)],
        limit: numEventsToRequest,
        excludeEvents: excludedEvents,
        includeEvents: includedEvents,
        dataGroupID: String(this.attr(`data-group-id`)),
      });
      if (error) {
        this.update({
          error: true,
          loading: {
            ...this.state.loading,
            initial: false,
          },
        });
        this.queryApiPool.release();
        return;
      }

      const {rawEvents} = this.state;

      const {
        shouldRerequest,
        numEventsToRequest: newNumEventsToRequest,
        isAtHistoryLimit,
        enableShowMoreButton,
        earliestTime,
        events,
      } = getActivityStreamFieldsToUpdate(rawEvents, newEvents, {
        historyLimit,
        fromDate,
        numberOfEventsDisplayed,
        numEventsToRequest,
      });

      this.update({
        numEventsToRequest: newNumEventsToRequest,
        isAtHistoryLimit,
        enableShowMoreButton,
        earliestTime,
        rawEvents: events,
        loading: {
          ...this.state.loading,
          initial: shouldRerequest,
        },
      });

      this.queryApiPool.release();

      if (shouldRerequest) {
        this.loadUserActivity();
      } else {
        this.trackEvent(TrackedEvents.QueryFinished, this.activityStreamTrackingProperties());
      }
    }
  }

  /**
   * This endpoint takes a to_date and a from_date, and returns the last N events to occur, in chronological order.
   * E.g. if requested with { fromDate: yesterday, toDate: today, limit: 100 events} you will get the last 100 events to
   * occur today, in ascending timestamp order (oldest to newest). If there are less than 100 events today, you will get
   * all N events from today, and the last 100-N events from yesterday.
   */
  private async fetchUserActivity(
    params: ProfileQueryParameters,
  ): Promise<{error: boolean; events: UnformattedUserEvent[]}> {
    const result = await profileClient.fetchProfileEndpoint<ActivityStreamQueryResult>(
      params,
      ProfileQueryEndpoint.ActivityStream,
    );
    if (result.error || result?.results?.status !== `ok`) {
      return {
        error: true,
        events: null,
      };
    }

    const results = result.results.results;

    return {
      error: false,
      events: results.events,
    };
  }

  dayHeader(comparisonTime: Moment): string {
    const now = epochTimestampToProjectTimeZone(Date.now(), this.attr(`timezone`));
    const formattedTime = comparisonTime.format(DISPLAY_DATE_FORMAT_STRING);

    if (comparisonTime.isSame(now, `day`)) {
      return `Today · ` + formattedTime;
    } else if (comparisonTime.isSame(now.clone().subtract(1, `day`), `day`)) {
      return `Yesterday · ` + formattedTime;
    }

    return formattedTime;
  }

  dateForEvent(time: number): Moment {
    const date = epochTimestampToProjectTimeZone(time * 1000, this.attr(`timezone`));

    return date.startOf(`day`);
  }

  groupActivityByDayAndEvent(events: FormattedUserEvent[]): ActivityDayGroup[] {
    const reversedEvents = events.reverse();
    const groupedByDate: FormattedUserEvent[][] = groupNeighboringEvents(reversedEvents, (a, b) =>
      this.dateForEvent(a.time).isSame(this.dateForEvent(b.time), `day`),
    );

    const groupedByEvent: FormattedUserEvent[][][] = groupedByDate.map((eventGroup) =>
      groupNeighboringEvents(eventGroup, (a, b) => a.eventDisplayName === b.eventDisplayName),
    );

    return groupedByEvent.map(
      (group: FormattedUserEvent[][]): ActivityDayGroup => ({
        dayHeader: this.dayHeader(this.dateForEvent(group[0][0].time)),
        activities: group.map(
          (group): ActivityGroup => ({
            eventName: group[0].eventDisplayName,
            eventGroup: group,
          }),
        ),
      }),
    );
  }

  activityStreamTrackingProperties(): TrackedProperties {
    const {dateRange, includedEvents, excludedEvents} = this.state;

    const {from, to} = dateRange.getCalendarDatesAsOf(this.attr(`project-as-of-date`));

    const selectivelyShowingEvents = includedEvents.length > 0;

    return {
      'Selectively Showing Events': selectivelyShowingEvents,
      '# of Included Events': selectivelyShowingEvents ? includedEvents.length : null,
      '# of Excluded Events': selectivelyShowingEvents ? null : excludedEvents.length,
      'Date range type': dateRange.type,
      'Date range unit': dateRange.window?.unit,
      'Date range value': dateRange.window?.value,
      'Date range from': from,
      'Date range to': to,
    };
  }

  // TODO(sam): use typescript template string types for typing instead of string once https://github.com/microsoft/TypeScript/pull/40336 is released
  // We can type is as ```type EventName<T extends string> = `[mp-query-builder]${T}`; ```
  trackEvent(eventName: TrackedEvents | string, properties: TrackedProperties): void {
    emitTrackEvent(this, {
      eventName,
      props: properties,
    });
  }
}

export const ProfileActivityEl = defineAndMakeJsxFactory<ProfileActivityJsxProps>(`profile-activity`, ProfileActivity);
export const ProfileActivityReact = panel2react<ProfileActivityJsxProps>(`profile-activity`, ProfileActivity);
